/**
 * Fetch wrapper: attaches the JWT (from localStorage) as a Bearer header,
 * JSON-encodes request bodies (passing FormData straight through so the
 * browser sets its own multipart boundary), JSON-decodes responses, and on
 * a 401 from an *authenticated* request (token was present) clears the
 * stored token and calls the (overridable) unauthorized handler.
 *
 * A 401 on an *unauthenticated* call (e.g. a bad login attempt, which has no
 * token to begin with) is NOT treated as a session expiry — it's returned
 * as a normal ApiError so the Login screen can show "invalid credentials"
 * instead of bouncing back to itself.
 */

import { resolveUrl, isNative } from "./native/platform";
import { saveAndShare } from "./native/share";
// Token/user persistence lives in tokenStore: localStorage on web, Android
// Keystore-backed secure storage on native (with a synchronous in-memory
// cache so getToken() stays sync here). Re-exported so existing imports of
// these names from "./api" keep working unchanged.
import {
  getToken,
  setToken,
  clearToken,
  getUser,
  setUser,
  type AuthUser,
} from "./native/tokenStore";

export { getToken, setToken, clearToken, getUser, setUser };
export type { AuthUser };

/**
 * Called after clearToken() on a session-expiry 401. Defaults to a hard
 * redirect; App.tsx overrides this at startup with a react-router `navigate`
 * so the SPA doesn't full-reload. Kept overridable (rather than importing
 * react-router here) so this module has zero router dependency and the
 * required vitest wrapper test can assert on it directly.
 */
let unauthorizedHandler: () => void = () => {
  window.location.assign("/login");
};

export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    const message =
      body && typeof body === "object" && "error" in (body as Record<string, unknown>)
        ? String((body as Record<string, unknown>).error)
        : `request failed (${status})`;
    super(typeof message === "string" ? message : JSON.stringify(message));
    this.status = status;
    this.body = body;
  }
}

/**
 * Shared side-effect for a 401 response: only a token that WAS present (a
 * real session expiring, not a bad-login attempt with no token to begin
 * with) clears it and calls the unauthorized handler. Both `request()` and
 * `fetchBlobUrl()` hit this on a 401; each keeps its own surrounding
 * throw/parse flow (they branch to it from different places — `request()`
 * checks 401 before its general `!res.ok`, `fetchBlobUrl()` checks it
 * inside `!res.ok`), so only this one side-effect is factored out.
 */
function handleUnauthorized(res: Response, token: string | null): void {
  if (res.status === 401 && token) {
    clearToken();
    unauthorizedHandler();
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

/** Core request: `path` is relative, e.g. "/api/parties". */
export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body; // browser sets multipart Content-Type + boundary itself
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }

  const res = await fetch(resolveUrl(path), { ...options, headers, body });

  if (res.status === 401) {
    handleUnauthorized(res, token);
    const parsed = await parseBody(res);
    throw new ApiError(401, parsed);
  }

  if (!res.ok) {
    const parsed = await parseBody(res);
    throw new ApiError(res.status, parsed);
  }

  if (res.status === 204) return undefined as T;
  return (await parseBody(res)) as T;
}

export const api = {
  get: <T = unknown>(path: string) => request<T>(path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T = unknown>(path: string) => request<T>(path, { method: "DELETE" }),
};

/**
 * Fetches an authenticated binary resource (e.g. a vault document) and
 * returns a blob object URL suitable for an <iframe>/<img> src. A plain
 * <iframe src="/api/documents/:id/file"> would fire without the
 * Authorization header (browsers don't attach JS-managed bearer tokens to
 * markup-driven requests) and 401 -- this routes it through fetch() instead.
 * Caller is responsible for `URL.revokeObjectURL` when done (e.g. on unmount).
 */
export async function fetchBlobUrl(path: string): Promise<{ url: string; mime: string }> {
  const token = getToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(resolveUrl(path), { headers });
  if (!res.ok) {
    handleUnauthorized(res, token);
    throw new ApiError(res.status, await parseBody(res));
  }
  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), mime: blob.type };
}

/**
 * Fetches an authenticated resource and triggers a browser download via a
 * temporary <a download> click -- same problem as fetchBlobUrl above but for
 * "Save As" links (khaata.xlsx / source-of-funds.xlsx exports) instead of
 * inline preview.
 */
export async function download(path: string, filename: string): Promise<void> {
  if (isNative()) {
    // WebView has no "Save As": fetch → write to storage → Android share sheet.
    await saveAndShare(path, filename);
    return;
  }
  const { url } = await fetchBlobUrl(path);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
